import React from 'react';
import LineChartCard from '../Chart/LineChartCard';
import CardSurface from '../Card/CardSurface';
import { getKpiFieldDisplay } from '../../utils/kpiFieldIcon';
import './KpiIndicatorCard.css';

/**
 * KpiIndicatorCard — KPI 1개 지표 카드 (정의 + 산식 + 현재/목표/이전 + 변동률 + 미니 추이).
 * 사용법: docs/modules/frontend/dashboard/KpiIndicatorCard.md
 * R4b-v2: 분야 아이콘 + 차트 확대 (220) + 변동률 + 큰 폰트 위계.
 */
const EM_DASH = '—';

const fmt = (v) =>
  v === null || v === undefined
    ? EM_DASH
    : typeof v === 'number' ? v.toLocaleString() : String(v);

const inRangeOf = (v, min, max) => {
  if (v === null || v === undefined) return true;
  if (typeof min === 'number' && v < min) return false;
  if (typeof max === 'number' && v > max) return false;
  return true;
};

const computeDelta = (current, previous) => {
  if (current === null || current === undefined) return { text: EM_DASH, sign: 'none' };
  if (previous === null || previous === undefined) return { text: EM_DASH, sign: 'none' };
  const prev = Number(previous);
  if (prev === 0) return { text: EM_DASH, sign: 'none' };
  const pct = ((Number(current) - prev) / prev) * 100;
  if (!Number.isFinite(pct)) return { text: EM_DASH, sign: 'none' };
  if (Math.abs(pct) < 0.05) return { text: '0.0%', sign: 'zero' };
  if (pct > 0) return { text: `▲ +${pct.toFixed(1)}%`, sign: 'positive' };
  // pct < 0 — U+2212 마이너스 부호. toFixed(-33.333) → '-33.3' 의 ASCII '-' 대신 '−'.
  return { text: `▼ −${Math.abs(pct).toFixed(1)}%`, sign: 'negative' };
};

const KpiIndicatorCard = ({
  indicator, currentValue, previousValue = null,
  formula = null, trend = [], loading = false, onClick,
}) => {
  const { indicatorName = '', unit = '', targetValue, rangeMin, rangeMax, kpiFldCd } = indicator || {};
  const inRange = inRangeOf(currentValue, rangeMin, rangeMax);
  const currentCls = `kpi-card-current ${inRange ? 'in-range' : 'out-of-range'}`;
  const clickable = typeof onClick === 'function';
  const safeTrend = Array.isArray(trend) ? trend : [];
  const { Icon, color: fieldColor } = getKpiFieldDisplay(kpiFldCd);
  const delta = computeDelta(currentValue, previousValue);
  const deltaCls = `kpi-card-delta delta-${delta.sign}`;
  // 기준일 = 최신 trend 항목의 날짜. KPI external 데이터는 날짜를 `name` 필드로 주므로 date→name 폴백.
  const lastTrend = safeTrend.length > 0 ? safeTrend[safeTrend.length - 1] : null;
  const lastDate = lastTrend ? (lastTrend.date ?? lastTrend.name ?? null) : null;

  return (
    <CardSurface data-testid="kpi-indicator-card" className="h-100 shadow-sm"
      style={clickable ? { cursor: 'pointer' } : undefined}
      onClick={clickable ? () => onClick(indicator) : undefined}
      role={clickable ? 'button' : undefined} tabIndex={clickable ? 0 : undefined}>
      <div className="card-body">
        {/* 헤더 — 분야 아이콘 + 지표명 + 단위 */}
        <div className="kpi-card-header">
          <span data-testid="kpi-card-field-icon" className="kpi-card-field-icon" style={{ color: fieldColor }}>
            <Icon size={20} />
          </span>
          <h6 className="card-title mb-0">{indicatorName}</h6>
          {unit ? <span className="kpi-card-unit text-muted small">{unit}</span> : null}
        </div>

        {/* 현재값 + 변동률 (큰 폰트 위계) */}
        <div className="kpi-card-current-row">
          <span data-testid="kpi-card-current" className={currentCls}
            style={{ fontSize: '2rem', fontWeight: 700 }}>
            {loading ? '…' : fmt(currentValue)}
          </span>
          <span data-testid="kpi-card-delta" className={deltaCls} title="이전값 대비 변동률">
            {delta.text}
          </span>
        </div>

        {lastDate ? (
          <div data-testid="kpi-card-date" className="kpi-card-date text-muted small">
            기준일 {lastDate}
          </div>
        ) : null}

        {/* 보조 정보 — 목표 / 이전 */}
        <div className="kpi-card-meta">
          <span className="kpi-card-target text-muted small">목표 {fmt(targetValue)}</span>
          <span data-testid="kpi-card-previous" className="kpi-card-previous text-muted small">
            이전 {fmt(previousValue)}
          </span>
        </div>

        {formula ? (
          <div data-testid="kpi-card-formula" className="kpi-card-formula small text-muted">{formula}</div>
        ) : null}

        {/* 차트 (확대) */}
        <div className="kpi-card-trend" data-testid="kpi-card-trend">
          {safeTrend.length === 0 ? (
            <span className="small text-muted">표시할 데이터가 없습니다</span>
          ) : (
            <LineChartCard data={safeTrend} xKey="date" yKey="value" width="100%" height={220} />
          )}
        </div>
      </div>
    </CardSurface>
  );
};

export default KpiIndicatorCard;
